import { getValidCustomDates, isWithinDays } from "main/availability-dates";
import { getAvailabilityLabel } from "main/availability-labels";
// frontend/entrypoints/product.js
var product_default = () => ({
	images: [],
	optionNames: [],
	priceVaries: null,
	defaultPrice: null,
	defaultCompareAtPrice: null,
	defaultDiscountPercentage: null,
	variants: [],
	price: null,
	compareAtPrice: null,
	discountPercentage: null,
	available: null,
	availability: null,
	nextDataDisponibilita: null,
	displayGiorniDisponibilita: null,
	quantity: 1,
	inventoryQuantity: null,
	inventoryPolicy: null,
	sellingPlan: null,
	dateDisponibilita: null,
	giorniDisponibilita: null,
	currentVariantId: null,
	currentVariantImageId: null,
	selectedOptions: {},
	selectedCustomOptions: {},
	allCustomOptionsChecked: true,
	optionAvailability: {},
	init() {
		this.initializeFromServer();
		this.computeOptionAvailability();
		this.applyInitialVariant();
		this.$watch("selectedOptions", () => this.onOptionChange(), { deep: true });
		this.$watch("selectedCustomOptions", () => {
			this.allCustomOptionsChecked = Object.values(this.selectedCustomOptions).every(Boolean);
		}, { deep: true });
		this.$nextTick(() => {
			this.updateDisponibilitaFromData();
		});
	},
	initializeFromServer() {
		const scriptEl = this.$el.querySelector("#product-data");
		const productData = this.parseJsonAttribute(scriptEl?.textContent);
		if (!productData) return;
		this.images = productData.images || [];
		this.optionNames = productData.optionNames || [];
		this.priceVaries = productData.priceVaries;
		this.defaultPrice = productData.defaultPrice;
		this.defaultCompareAtPrice = productData.defaultCompareAtPrice;
		this.defaultDiscountPercentage = productData.defaultDiscountPercentage;
		this.variants = productData.variants || [];
		if (productData.initialVariantId) {
			const initialVariant = this.variants.find((v) => v.id === productData.initialVariantId);
			if (initialVariant) this.optionNames.forEach((optionName, index) => {
				this.selectedOptions[optionName] = initialVariant.options[index];
			});
		}
	},
	applyInitialVariant() {
		const variant = this.findSelectedVariant();
		if (variant) this.applyVariantData(variant);
		else {
			this.price = this.defaultPrice;
			this.compareAtPrice = this.defaultCompareAtPrice;
			this.discountPercentage = this.defaultDiscountPercentage;
		}
	},
	onOptionChange() {
		this.computeOptionAvailability();
		const variant = this.findSelectedVariant();
		if (variant) {
			this.applyVariantData(variant);
			this.updateDisponibilitaFromData();
			if (this.currentVariantId) this.updateUrlVariant(this.currentVariantId);
			if (this.currentVariantImageId) this.scrollToVariantImage(this.currentVariantImageId);
		} else {
			this.currentVariantId = null;
			this.currentVariantImageId = null;
			this.available = null;
			this.availability = null;
			this.price = this.defaultPrice;
			this.compareAtPrice = this.defaultCompareAtPrice;
			this.discountPercentage = this.defaultDiscountPercentage;
		}
	},
	findSelectedVariant() {
		if (!this.optionNames.every((name) => this.selectedOptions[name])) return null;
		return this.variants.find((variant) => this.optionNames.every((name, index) => variant.options[index] === this.selectedOptions[name]));
	},
	computeOptionAvailability() {
		const newAvailability = {};
		this.optionNames.forEach((optionName, optionIndex) => {
			const upstreamFilters = {};
			for (let i = 0; i < optionIndex; i++) {
				const upstreamName = this.optionNames[i];
				if (this.selectedOptions[upstreamName]) upstreamFilters[i] = this.selectedOptions[upstreamName];
			}
			const hasAllUpstream = Object.keys(upstreamFilters).length === optionIndex;
			new Set(this.variants.map((v) => v.options[optionIndex])).forEach((value) => {
				const key = `${optionName}:${value}`;
				if (optionIndex > 0 && !hasAllUpstream) {
					newAvailability[key] = "x";
					return;
				}
				let bestAvailability = "x";
				for (const variant of this.variants) {
					if (variant.options[optionIndex] !== value) continue;
					let upstreamMatch = true;
					for (const [i, upstreamValue] of Object.entries(upstreamFilters)) if (variant.options[i] !== upstreamValue) {
						upstreamMatch = false;
						break;
					}
					if (!upstreamMatch) continue;
					const variantAvailability = this.computeVariantAvailability(variant);
					if (variantAvailability === "24h") {
						bestAvailability = "24h";
						break;
					} else if (variantAvailability === "5gg" && bestAvailability !== "24h") bestAvailability = "5gg";
					else if (variantAvailability === "custom-soon" && bestAvailability !== "24h" && bestAvailability !== "5gg") bestAvailability = "custom-soon";
					else if (variantAvailability === "custom" && bestAvailability !== "24h" && bestAvailability !== "5gg" && bestAvailability !== "custom-soon") bestAvailability = "custom";
				}
				newAvailability[key] = bestAvailability;
			});
		});
		this.optionAvailability = newAvailability;
	},
	computeVariantAvailability(variant) {
		if (!variant.available) return "x";
		if (variant.inventoryQuantity > 0) return "24h";
		if (variant.inventoryPolicy !== "continue") return "x";
		if (variant.dateDisponibilita || variant.giorniDisponibilita != null) {
			const validDates = getValidCustomDates(variant.dateDisponibilita, variant.inventoryQuantity);
			if (validDates.length > 0) return isWithinDays(validDates[0], 7) ? "custom-soon" : "custom";
			if (variant.giorniDisponibilita != null) return variant.giorniDisponibilita <= 7 ? "custom-soon" : "custom";
			return "x";
		}
		return variant.sellingPlan ? "x" : "5gg";
	},
	applyVariantData(variant) {
		this.currentVariantId = variant.id;
		this.currentVariantImageId = variant.imageId;
		this.price = variant.price;
		this.compareAtPrice = variant.compareAtPrice;
		this.discountPercentage = variant.discountPercentage;
		this.available = variant.available;
		this.inventoryQuantity = variant.inventoryQuantity;
		this.inventoryPolicy = variant.inventoryPolicy;
		this.sellingPlan = variant.sellingPlan;
		this.dateDisponibilita = variant.dateDisponibilita;
		this.giorniDisponibilita = variant.giorniDisponibilita;
	},
	updateDisponibilitaFromData() {
		this.nextDataDisponibilita = null;
		this.displayGiorniDisponibilita = null;
		if (!this.currentVariantId) {
			this.availability = null;
			return;
		}
		this.availability = this.computeVariantAvailability(this);
		if (this.availability !== "custom" && this.availability !== "custom-soon") return;
		const validDates = getValidCustomDates(this.dateDisponibilita, this.inventoryQuantity);
		if (validDates.length > 0) this.nextDataDisponibilita = validDates[0].toLocaleDateString(document.documentElement.lang || void 0, {
			day: "2-digit",
			month: "long",
			year: "numeric"
		});
		else if (this.giorniDisponibilita) this.displayGiorniDisponibilita = this.giorniDisponibilita;
	},
	parseJsonAttribute(jsonString) {
		if (!jsonString) return null;
		try {
			return JSON.parse(jsonString);
		} catch (e) {
			console.error("Failed to parse JSON:", e);
			return null;
		}
	},
	getOptionClass(optionName, value, optionPosition) {
		const isSelected = this.selectedOptions[optionName] === value;
		if (optionPosition === 2 && !this.selectedOptions[this.optionNames[0]] || optionPosition === 3 && (!this.selectedOptions[this.optionNames[0]] || !this.selectedOptions[this.optionNames[1]])) return "border-gray-200 bg-gray-100 text-gray-400";
		if (isSelected) return "border-gray-300 bg-black text-white";
		return {
			"24h": "border-gray-300 bg-green-300 text-black hover:bg-green-300/70",
			"5gg": "border-gray-300 bg-green-200 text-black hover:bg-green-200/70",
			"custom-soon": "border-gray-300 bg-green-200 text-black hover:bg-green-200/70",
			custom: "border-gray-300 bg-yellow-300 text-black hover:bg-yellow-300/70"
		}[this.optionAvailability[`${optionName}:${value}`] || "x"] || "border-gray-300 bg-gray-200 text-gray-300 hover:bg-gray-200/70";
	},
	getOptionAvailabilityLabel(optionName, value, optionPosition) {
		if (optionPosition === 2 && !this.selectedOptions[this.optionNames[0]] || optionPosition === 3 && (!this.selectedOptions[this.optionNames[0]] || !this.selectedOptions[this.optionNames[1]])) return "";
		return getAvailabilityLabel(this.optionAvailability[`${optionName}:${value}`] || "x");
	},
	updateUrlVariant(variantId) {
		const url = new URL(window.location.href);
		url.searchParams.set("variant", variantId);
		window.history.replaceState({}, "", url);
	},
	scrollToVariantImage(imageId) {
		if (!imageId) return;
		const imageIndex = this.images.findIndex((img) => img.id === imageId);
		if (imageIndex !== -1) this.$nextTick(() => this.$dispatch("scroll-to-slide", imageIndex));
	},
	initGallery() {
		if (this.currentVariantImageId) this.$nextTick(() => this.scrollToVariantImage(this.currentVariantImageId));
	}
});
export { product_default as default };
